import { Table } from '@/components/ui/Table'
import { ROUTES, buildRoute } from '@/constants/routes'
import { Avatar, Box, HStack, IconButton } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useState } from 'react'
import { FiEdit, FiTrash2 } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { LEAD_SOURCE_LABELS, LEAD_STATE_LABELS } from '../constants/leadEnums'
import { StatusBadge } from './StatusBadge'

const columnHelper = createColumnHelper()

/**
 * Leads Table Component
 * Displays leads in a table with sorting and pagination
 */
export function LeadsTable({
  data = [],
  isLoading,
  pageCount,
  totalCount,
  pagination,
  sorting,
  onPaginationChange,
  onSortingChange,
  onDelete,
}) {
  const navigate = useNavigate()
  const [rowSelection, setRowSelection] = useState({})

  // React 19 compiler automatically optimizes - no need for useMemo
  const columns = [
    // Checkbox column
    columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        const allSelected = table.getIsAllRowsSelected()
        const someSelected = table.getIsSomeRowsSelected()
        return (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
            style={{
              cursor: 'pointer',
              width: '16px',
              height: '16px',
              accentColor: '#0052CC',
              borderRadius: '3px',
            }}
          />
        )
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          style={{
            cursor: row.getCanSelect() ? 'pointer' : 'not-allowed',
            width: '16px',
            height: '16px',
            accentColor: '#0052CC',
            borderRadius: '3px',
            opacity: row.getCanSelect() ? 1 : 0.5,
          }}
        />
      ),
      meta: {
        width: '48px',
      },
    }),
    columnHelper.accessor('title', {
        header: 'Title',
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue() || '-'
          const lead = info.row.original
          return (
            <Box
              as="span"
              color="#0052CC"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
              onClick={() => navigate(buildRoute(ROUTES.LEADS_EDIT, { uuid: lead.uuid }))}
            >
              {value}
            </Box>
          )
        },
      }),
      columnHelper.accessor('first_name', {
        header: 'First Name',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('last_name', {
        header: 'Last Name',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('phone', {
        header: 'Phone',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        enableSorting: false,
        cell: (info) => <StatusBadge status={ info.getValue() } />,
      }),
      columnHelper.accessor('state', {
        header: 'State',
        enableSorting: false,
        cell: (info) => LEAD_STATE_LABELS[info.getValue()] || info.getValue(),
      }),
      columnHelper.accessor('source', {
        header: 'Source',
        enableSorting: false,
        cell: (info) => LEAD_SOURCE_LABELS[info.getValue()] || info.getValue(),
      }),
      columnHelper.accessor('assigned_to', {
        header: 'Assigned To',
        enableSorting: false,
        cell: (info) => {
          const user = info.getValue()

          // If no user assigned
          if (!user) return '-'

          // If user is just an ID (backend didn't expand) - fallback
          if (typeof user === 'number') {
            return `User #${user}`
          }

          // If user is a string
          if (typeof user === 'string') {
            return user
          }

          // If user is an object - show with avatar (expected from backend)
          const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email
          const initials = (user.first_name?.[0] || '') + (user.last_name?.[0] || '') || user.username?.[0]?.toUpperCase() || '?'

          return (
            <HStack spacing={ 2 }>
              <Avatar.Root size="xs" width="24px" height="24px">
                <Avatar.Image src={ user.avatar } />
                <Avatar.Fallback fontSize="10px" fontWeight="600">
                  { initials }
                </Avatar.Fallback>
              </Avatar.Root>
              <Box as="span" fontSize="13px" color="#172B4D" fontWeight="500" noOfLines={ 1 }>
                { displayName }
              </Box>
            </HStack>
          )
        },
      }),
      columnHelper.accessor('next_action_due_at', {
        header: 'Next Action',
        cell: (info) => {
          const date = info.getValue()
          return date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : '-'
        },
      }),
      columnHelper.accessor('updated_at', {
        header: 'Updated',
        cell: (info) => {
          const date = info.getValue()
          return date ? format(new Date(date), 'dd.MM.yyyy') : '-'
        },
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => {
          const date = info.getValue()
          return date ? format(new Date(date), 'dd.MM.yyyy') : '-'
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const lead = info.row.original
          return (
            <HStack spacing={ 1 }>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Edit lead"
                onClick={ () => navigate(buildRoute(ROUTES.LEADS_EDIT, { uuid: lead.uuid })) }
              >
                <Box as={ FiEdit } />
              </IconButton>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Delete lead"
                onClick={ () => onDelete?.(lead) }
              >
                <Box as={ FiTrash2 } />
              </IconButton>
            </HStack>
          )
        },
      }),
    ]

  return (
    <Box>
      <Table
        data={ data }
        columns={ columns }
        isLoading={ isLoading }
        enableSorting
        enablePagination
        enableRowSelection
        manualPagination
        manualSorting
        pageCount={ pageCount }
        totalCount={ totalCount }
        getRowId={(row) => row.uuid}
        state={{
          rowSelection,
          pagination,
          sorting,
        }}
        onRowSelectionChange={setRowSelection}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
      />
    </Box>
  )
}

export default LeadsTable
